import { useAuth } from "@hooks/useAuth";
import { useDataMutation } from "@hooks/useData/useDataMutation";
import useDialog from "@hooks/useDialog";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import generateAssetUrl from "@utils/generateAssetUrl";
import { imageToBase64 } from "@utils/imageToBase64";
import { serverEndpoints } from "@utils/serverEndpoints";
import { authClient } from "@utils/authClient";
import { getPagination, selectData, softDelete, supabase } from "@utils/serverClient";
import { useEffect, useState } from "react";
import { Asset, UpdateAsset } from "types";

export type AssetWithUrl = Asset & {
  url?: string;
};

export const usePublicAssets = () => {
  const paginate = true
  const { showErrorSnackbar } = useDialog();
  const [pageSize, setPageSize] = useState(30);
  const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined);
  const [assets, setAssets] = useState<AssetWithUrl[]>([]);

  const { organization } = useAuth();

  const queryClient = useQueryClient();

  const queryKey = ["public_assets", searchQuery, paginate]

  const { mutate: uploadAsset, isPending: isUploading } = useDataMutation({
    mutationFn: async (acceptedFiles: File[]) => {
      if (!organization?.id) return;
      if (acceptedFiles.length === 0) return;
      if (acceptedFiles.length > 1) {
        return showErrorSnackbar("Please select only one asset at a time.");
      }

      const serverUrl = serverEndpoints.uploadAsset;
      const acceptedFile = acceptedFiles[0];

      const fileExtension = acceptedFile.name.split(".")[1];
      const fileName = acceptedFile.name.split(".")[0];
      const bitmap = await createImageBitmap(acceptedFile);
      const dimension = `${bitmap.width}x${bitmap.height}`;

      const metadata = {
        name: fileName,
        dimension,
        extension: fileExtension,
        org_id: organization.id,
        is_public: true,
      };

      const accessToken = await authClient.getAccessToken();
      const base64Data = await imageToBase64(acceptedFile);
      const response = await fetch(serverUrl, {
        method: "POST",
        body: JSON.stringify({
          file: base64Data,
          metadata
        }),
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to upload thumbnail");
      }

      return response.json()
    },
    queryKeyToInvalidate: queryKey,
    errorMessage: "Failed to upload asset",
    optimisticUpdateFn: (oldData: Asset[] | undefined, [newFile]) => {
      const newAsset: Asset = {
        id: `temp-${Date.now()}`,
        name: newFile.name.split(".").slice(0, -1).join("."),
        created_at: new Date().toISOString(),
        deleted: false,
        dimension: null,
        extension: newFile.name.split(".").slice(-1)[0],
        org_id: null,
        tags: [],
        updated_at: null,
        // @ts-ignore - This is a temporary client-side only property
        url: URL.createObjectURL(newFile)
      }
      return [newAsset, ...(oldData ?? [])]
    },
  })

  const {
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    data,
    status: assetsFetchStatus,
  } = useInfiniteQuery({
    queryKey,
    refetchOnMount: false,
    initialPageParam: 0,
    queryFn: async ({ pageParam = 0 }) => {
      if (pageParam === undefined) return;

      const { from, to } = getPagination(pageParam, pageSize);

      const { data, error } = paginate
        ? searchQuery
          ? await selectData({
            table: "asset",
            columns: `*`,
          })
            .is("org_id", null)
            .order("created_at", { ascending: false })
            .range(from, to)
            .textSearch("name", searchQuery)
          : await selectData({
            table: "asset",
            columns: `*`,
          })
            .is("org_id", null)
            .order("created_at", { ascending: false })
            .range(from, to)
        : await selectData({
          table: "asset",
          columns: `*`,
        })
          .is("org_id", null)
          .order("created_at", { ascending: false });

      if (error || !data) return;
      return data.map((e) => ({
        ...e,
        url: generateAssetUrl(e.id, 75),
      }));
    },

    getNextPageParam: (_lastGroup, groups) =>
      (_lastGroup ?? [])?.length < pageSize ? undefined : groups.length,
  });

  const { mutate: updateAsset, isPending: isUpdating } = useDataMutation({
    mutationFn: async (asset: UpdateAsset) => {
      const { error } = await supabase
        .from("asset")
        .update(asset)
        .eq("id", asset.id);

      if (error) {
        throw new Error(error.message);
      }
      return null;
    },
    queryKeyToInvalidate: queryKey,
    errorMessage: "Failed to update asset",
    optimisticUpdateFn: (oldData: Asset[] | undefined, _asset) => {
      if (!oldData) return oldData;
      return oldData.map((asset) =>
        asset.id === asset.id ? { ...asset, ..._asset } : asset
      );
    },
  });

  const { mutate: deleteAsset } = useDataMutation({
    mutationFn: async (assetId: string) => {
      const { error } = await softDelete({
        table: "asset",
      }).match({ id: assetId });
      if (error) throw error;
      return assetId;
    },
    queryKeyToInvalidate: queryKey,
    errorMessage: "Failed to delete asset",
    optimisticUpdateFn: (oldData: Asset[] | undefined, assetId) => {
      if (!oldData) return oldData;
      return oldData.filter((asset) => asset.id !== assetId);
    },
  });

  function getAssets() {
    queryClient.invalidateQueries({ queryKey });
  }

  useEffect(() => {
    if (data) {
      setAssets(data?.pages.flatMap((e) => e!) ?? []);
    }
  }, [data]);

  return {
    assets,
    assetsFetchStatus,
    updateAsset,
    deleteAsset,
    isUpdating,
    getAssets,
    hasNextPage,
    pageSize,
    setPageSize,
    searchQuery,
    setSearchQuery,
    isFetchingNextPage,
    fetchNextPage,
    uploadAsset,
    isUploading,
  };
};
